
import React, { useState, useMemo, useEffect } from 'react';
import { Employee } from '../types';
import Modal from './Modal'; // Assuming Modal.tsx is in the same directory or correct path
import HelpIcon from './HelpIcon';

interface AssignEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  allEmployees: Employee[];
  currentAssignedEmployeeId: string | null;
  onAssign: (employeeId: string | null) => void;
  stationNumberDisplayName: string;
  currentLineId?: string;
  currentShift?: string;
}

const AssignEmployeeModal: React.FC<AssignEmployeeModalProps> = ({
  isOpen,
  onClose,
  allEmployees,
  currentAssignedEmployeeId,
  onAssign,
  stationNumberDisplayName,
  currentLineId,
  currentShift,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  // Load the hideAssigned preference from localStorage, default to true
  const [hideAssigned, setHideAssigned] = useState(() => {
    try {
      const saved = localStorage.getItem('hideAssignedEmployees');
      console.log('AssignEmployeeModal - Loading from localStorage:', saved);
      return saved !== null ? saved === 'true' : true; // Default to true if not set
    } catch (error) {
      console.error('AssignEmployeeModal - Error reading localStorage:', error);
      return true; // Default to true on error
    }
  });
  
  // Additional useEffect to ensure localStorage is properly loaded
  useEffect(() => {
    try {
      const saved = localStorage.getItem('hideAssignedEmployees');
      console.log('AssignEmployeeModal - useEffect checking localStorage:', saved);
      if (saved !== null && (saved === 'true') !== hideAssigned) {
        console.log('AssignEmployeeModal - Syncing state with localStorage:', saved === 'true');
        setHideAssigned(saved === 'true');
      }
    } catch (error) {
      console.error('AssignEmployeeModal - Error in useEffect localStorage:', error);
    }
  }, []); // Only run once on mount
  
  // Save the preference whenever it changes
  const handleHideAssignedChange = (checked: boolean) => {
    console.log('AssignEmployeeModal - Saving to localStorage:', checked);
    setHideAssigned(checked);
    try {
      localStorage.setItem('hideAssignedEmployees', checked.toString());
      console.log('AssignEmployeeModal - Successfully saved to localStorage:', checked.toString());
    } catch (error) {
      console.error('AssignEmployeeModal - Error saving to localStorage:', error);
    }
  };
  
  // Helper function to format shift display
  const formatShift = (shift: string): string => {
    if (!shift) return '';
    // Replace SHIFT_1 with Shift 1, SHIFT_2 with Shift 2, etc.
    const formatted = shift.replace(/SHIFT_(\d+)/, 'Shift $1');
    console.log(`AssignEmployeeModal - Formatting shift: "${shift}" -> "${formatted}"`);
    return formatted;
  };

  const filteredEmployees = useMemo(() => {
    let filtered = allEmployees;
    
    // Debug logging
    console.log('AssignEmployeeModal - All employees:', allEmployees.length);
    console.log('AssignEmployeeModal - Current shift:', currentShift);
    console.log('AssignEmployeeModal - Current line ID:', currentLineId);
    console.log('AssignEmployeeModal - Current assigned employee ID:', currentAssignedEmployeeId);
    
    // Deep check of employee data
    if (allEmployees.length > 0) {
      console.log('AssignEmployeeModal - First employee full object:', allEmployees[0]);
      console.log('AssignEmployeeModal - Employee keys:', Object.keys(allEmployees[0]));
    }
    
    const assignedCount = allEmployees.filter(emp => emp.currentAssignment).length;
    console.log('AssignEmployeeModal - Employees with assignments:', assignedCount);
    if (assignedCount > 0 && allEmployees.length > 0) {
      const firstAssigned = allEmployees.find(emp => emp.currentAssignment);
      console.log('AssignEmployeeModal - Example assignment:', firstAssigned?.currentAssignment);
      console.log('AssignEmployeeModal - Example assigned employee full object:', firstAssigned);
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(emp =>
        emp.firstName.toLowerCase().includes(lowerSearchTerm) ||
        emp.lastName.toLowerCase().includes(lowerSearchTerm) ||
        emp.employeeId.toLowerCase().includes(lowerSearchTerm)
      );
    }
    
    // Filter out assigned employees if checkbox is checked
    if (hideAssigned) {
      filtered = filtered.filter(emp => 
        !emp.currentAssignment || 
        emp.id === currentAssignedEmployeeId
      );
    }
    
    return filtered;
  }, [allEmployees, searchTerm, hideAssigned, currentShift, currentAssignedEmployeeId, currentLineId]);

  const handleSelectEmployee = (employeeId: string) => {
    onAssign(employeeId);
  };

  const handleUnassign = () => {
    onAssign(null);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={`Assign Employee to ${stationNumberDisplayName}`}
      size="md"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <input
            type="text"
            placeholder="Search employees by name or ID..."
            className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search employees"
          />
          
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={hideAssigned}
              onChange={(e) => handleHideAssignedChange(e.target.checked)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span>Hide already assigned employees</span>
            <HelpIcon section="stations/assigning-employees" tooltip="Learn about employee assignment" className="w-4 h-4" />
          </label>
        </div>

        <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md">
          {filteredEmployees.length > 0 ? (
            filteredEmployees.map(emp => {
              // Show as assigned if they have ANY assignment (not just same shift)
              // This way we can see all assignments across all shifts
              const isAssignedElsewhere = emp.currentAssignment && 
                emp.id !== currentAssignedEmployeeId;
              const isCurrentlyAssigned = emp.id === currentAssignedEmployeeId;
              
              console.log(`Employee ${emp.firstName} ${emp.lastName}: currentAssignment=`, emp.currentAssignment, 
                         'isAssignedElsewhere=', isAssignedElsewhere, 
                         'currentShift=', currentShift,
                         'isCurrentlyAssigned=', isCurrentlyAssigned);
              
              return (
                <button
                  key={emp.id}
                  onClick={() => handleSelectEmployee(emp.id)}
                  disabled={isAssignedElsewhere}
                  className={`w-full text-left px-3 py-2.5 transition-colors flex justify-between items-center
                    ${isCurrentlyAssigned 
                      ? 'bg-indigo-100 text-indigo-700 font-semibold hover:bg-indigo-200' 
                      : isAssignedElsewhere
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : 'text-slate-800 hover:bg-indigo-50'
                    }`}
                  aria-pressed={isCurrentlyAssigned}
                >
                  <span>
                    {emp.firstName} {emp.lastName} 
                    <span className="text-sm text-slate-500 ml-1">({emp.employeeId})</span>
                  </span>
                  {isAssignedElsewhere && emp.currentAssignment && (
                    <span className="text-xs text-gray-600 bg-gray-200 px-2 py-1 rounded">
                      {emp.currentAssignment.lineName} - Station {emp.currentAssignment.stationNumber} ({emp.currentAssignment.shift === 'SHIFT_1' ? 'Shift 1' : emp.currentAssignment.shift === 'SHIFT_2' ? 'Shift 2' : emp.currentAssignment.shift})
                    </span>
                  )}
                  {isCurrentlyAssigned && (
                    <span className="text-xs text-indigo-600 bg-indigo-200 px-2 py-1 rounded">
                      Current
                    </span>
                  )}
                </button>
              );
            })
          ) : (
            <p className="px-3 py-2 text-slate-500 italic">No employees match your search.</p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center pt-4 border-t border-slate-200 space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            type="button"
            onClick={handleUnassign}
            disabled={currentAssignedEmployeeId === null}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Unassign Employee
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AssignEmployeeModal;
